import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGamification } from '../../contexts/GamificationContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiAward, FiCalendar, FiUsers, FiBriefcase } = FiIcons;

const ActivityFeed = () => {
  const [activities, setActivities] = useState([]);
  const { addPoints, awardBadge } = useGamification();

  // Add activity to feed
  const addActivity = (type, description, points = 0, metadata = {}) => {
    const newActivity = {
      id: Date.now(),
      type,
      description,
      points,
      metadata,
      timestamp: new Date().toISOString(),
    };
    
    setActivities(prev => [newActivity, ...prev].slice(0, 10)); // Keep only last 10 activities
    
    // Add points if specified
    if (points > 0) {
      addPoints(type);
    }
  };

  // Get activity icon and color
  const getActivityInfo = (type) => {
    switch (type) {
      case 'profileCompletion':
        return { icon: FiUsers, color: 'bg-blue-100 text-blue-600' };
      case 'cvUpload':
        return { icon: FiAward, color: 'bg-green-100 text-green-600' };
      case 'interviewRequested':
        return { icon: FiCalendar, color: 'bg-purple-100 text-purple-600' };
      case 'jobPosted':
        return { icon: FiBriefcase, color: 'bg-orange-100 text-orange-600' };
      case 'badgeEarned':
        return { icon: FiAward, color: 'bg-yellow-100 text-yellow-600' };
      case 'dailyLogin':
        return { icon: FiTrendingUp, color: 'bg-indigo-100 text-indigo-600' };
      default:
        return { icon: FiTrendingUp, color: 'bg-gray-100 text-gray-600' };
    }
  };

  // Mock recent activities
  useEffect(() => {
    const mockActivities = [
      {
        id: 1,
        type: 'profileCompletion',
        description: 'Completed your profile',
        points: 50,
        timestamp: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
      },
      {
        id: 2,
        type: 'dailyLogin',
        description: 'Daily login bonus',
        points: 5,
        timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
      },
      {
        id: 3,
        type: 'badgeEarned',
        description: 'Earned "Profile Pioneer" badge',
        points: 25,
        timestamp: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
      },
    ];
    setActivities(mockActivities);
  }, []);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
      
      <div className="space-y-3">
        <AnimatePresence>
          {activities.map((activity, index) => {
            const activityInfo = getActivityInfo(activity.type);
            
            return (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
              >
                <div className={`p-2 rounded-lg ${activityInfo.color}`}>
                  <SafeIcon icon={activityInfo.icon} className="text-sm" />
                </div>
                
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{activity.description}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(activity.timestamp).toLocaleString()}
                  </p>
                </div>
                
                {activity.points > 0 && (
                  <div className="text-right">
                    <p className="text-sm font-semibold text-green-600">+{activity.points}</p>
                    <p className="text-xs text-gray-500">pts</p>
                  </div>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {activities.length === 0 && (
        <div className="text-center py-8">
          <SafeIcon icon={FiCalendar} className="text-gray-400 text-4xl mx-auto mb-3" />
          <p className="text-gray-600">No recent activity. Start completing tasks to earn points!</p>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-gray-200">
        <p className="text-sm text-gray-500 text-center">
          Complete activities to earn points and level up!
        </p>
      </div>
    </div>
  );
};

export default ActivityFeed;